import type { ComponentType, ForwardRefExoticComponent, RefAttributes } from "react";
import { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import type {
  DropdownIndicatorProps,
  DummyInputProps,
  GroupBase,
  InputProps,
  OptionProps,
  PlaceholderProps,
  Props as SelectReactProps,
  SelectInstance,
  SingleValueProps,
} from "@des/react-select";
import ReactSelect, { components } from "@des/react-select";

import type { DirectionalIconComponent } from "@jti/brand-icons";
import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { UiCheckIcon } from "@jti/brand-icons/ui-check";

import type { InputWrapperProps } from "../components";
import { InputWrapper } from "../components";

import styles from "./Select.module.scss";

type SelectOptionBase = {
  label: string;
  value: string;
  readonly?: boolean;
};

type SelectIconOption = SelectOptionBase & {
  Icon: DirectionalIconComponent;
  imageUrl?: never; // Ensures mutually exclusive properties
};

type SelectImageOption = SelectOptionBase & {
  imageUrl: string;
  Icon?: never;
};

type SelectStandardOption = SelectOptionBase & {
  Icon?: never;
  imageUrl?: never;
};

export type SelectOptionItem = SelectIconOption | SelectImageOption | SelectStandardOption;

type SelectDefaultProps = SelectReactProps<SelectOptionItem, false, GroupBase<SelectOptionItem>>;

export type SelectProps = Omit<SelectDefaultProps, "options" | "defaultValue"> &
  Pick<
    InputWrapperProps,
    | "label"
    | "helperText"
    | "isHelperTextVisible"
    | "isError"
    | "errorText"
    | "isOptional"
    | "containerClassName"
    | "wrapperClassName"
    | "showRequiredAsterisk"
  > & {
    options: SelectOptionItem[];
    defaultValue?: SelectOptionItem;
    dataTestId?: string;
    inputId?: string;
    hasLeadingIcon?: boolean;
  };

type SelectGroup = GroupBase<SelectOptionItem>;
type SelectInstance$ = SelectInstance<SelectOptionItem, false, SelectGroup>;

const KEYBOARD_FOCUS_KEYS = new Set(["ArrowDown", "ArrowUp", "End", "Enter", "Home", "PageDown", "PageUp", " "]);

const DropdownIndicatorComponent: ComponentType<DropdownIndicatorProps<SelectOptionItem, false, SelectGroup>> = (
  props
) => (
  <components.DropdownIndicator {...props}>
    <ChevronDownIcon aria-hidden="true" />
  </components.DropdownIndicator>
);

const withExposedComboboxValue = (
  BaseInput: ComponentType<InputProps<SelectOptionItem, false, SelectGroup>>
): ComponentType<InputProps<SelectOptionItem, false, SelectGroup>> => {
  const ExposedComboboxValueInput = (props: InputProps<SelectOptionItem, false, SelectGroup>) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const { innerRef } = props;
    const selectedOption = props.getValue()[0];
    const shouldExposeSelectedValue = props.value === "" && selectedOption !== undefined;

    const setInputRef = useCallback(
      (input: HTMLInputElement | null) => {
        inputRef.current = input;
        innerRef?.(input);
      },
      [innerRef]
    );

    useEffect(() => {
      const input = inputRef.current;

      if (!input || !shouldExposeSelectedValue) {
        return;
      }

      const selectExposedValue = () => input.select();

      input.addEventListener("click", selectExposedValue);

      if (!props.isHidden && document.activeElement === input) {
        input.select();
      }

      return () => input.removeEventListener("click", selectExposedValue);
    }, [props.isHidden, selectedOption, shouldExposeSelectedValue]);

    // Expose the selected option's label as the combobox value (WCAG 4.1.2 Name, Role, Value) while the user is not typing
    return (
      <BaseInput
        {...props}
        innerRef={setInputRef}
        isHidden={props.isHidden || shouldExposeSelectedValue}
        value={shouldExposeSelectedValue ? selectedOption.label : props.value}
      />
    );
  };

  return ExposedComboboxValueInput;
};

const withExposedDummyComboboxValue = (
  BaseInput: ComponentType<DummyInputProps<SelectOptionItem, false, SelectGroup>>
): ComponentType<DummyInputProps<SelectOptionItem, false, SelectGroup>> =>
  function ExposedDummyComboboxValueInput(props) {
    const selectedOption = props.getValue()[0];

    return <BaseInput {...props} value={selectedOption?.label ?? props.value} />;
  };

const OptionComponent: ComponentType<OptionProps<SelectOptionItem, false, SelectGroup>> = (props) => {
  return (
    <components.Option
      {...props}
      className={classNames(props.className, { [styles.readonlyOption]: props.data.readonly })}
    >
      <div className={styles.optionContent}>
        {props.data.imageUrl && (
          <img
            data-testid="selectOptionImage"
            src={props.data.imageUrl}
            alt={props.data.label}
            className={styles.optionImage}
          />
        )}
        {props.data.Icon && <props.data.Icon className={styles.optionIcon} aria-hidden="true" />}
        <div className={styles.optionLabel}>{props.label}</div>
        {props.isSelected && <UiCheckIcon className={styles.selectedOptionInsideListIcon} aria-hidden="true" />}
      </div>
    </components.Option>
  );
};

const PlaceholderComponent: ComponentType<PlaceholderProps<SelectOptionItem, false, SelectGroup>> = (props) => (
  <components.Placeholder {...props}>
    <UiCheckIcon className={styles.checkmarkIcon} aria-hidden="true" />
    <span>{props.children}</span>
  </components.Placeholder>
);

const SingleValueComponent: ComponentType<SingleValueProps<SelectOptionItem, false, SelectGroup>> = (props) => (
  <components.SingleValue {...props}>
    <div className={styles.selectedOptionContent}>
      {props.data.imageUrl && <img src={props.data.imageUrl} alt={props.data.label} className={styles.optionImage} />}
      {props.data.Icon ? (
        <props.data.Icon className={styles.selectedOptionIcon} aria-hidden="true" />
      ) : (
        <UiCheckIcon className={styles.checkmarkIcon} aria-hidden="true" />
      )}
      <span>{props.data.label}</span>
    </div>
  </components.SingleValue>
);

export const Select: ForwardRefExoticComponent<SelectProps & RefAttributes<SelectInstance$>> = forwardRef<
  SelectInstance$,
  SelectProps
>(
  (
    {
      id,
      inputId,
      label,
      helperText,
      dataTestId = "selectInput",
      isHelperTextVisible = false,
      isError,
      errorText,
      isOptional,
      containerClassName,
      wrapperClassName,
      hasLeadingIcon,
      showRequiredAsterisk,
      placeholder,
      classNames: selectClassNames,
      onKeyDown,
      onMenuClose,
      "aria-label": ariaLabel,
      components: providedComponents,
      ...rest
    },
    ref
  ) => {
    const { __ } = useTranslations();
    const generatedId = useId();
    const resolvedInputId = id ?? inputId ?? generatedId;
    const [isKeyboardFocusVisible, setIsKeyboardFocusVisible] = useState(false);

    const handleKeyDown: NonNullable<SelectDefaultProps["onKeyDown"]> = (event) => {
      if (KEYBOARD_FOCUS_KEYS.has(event.key)) {
        setIsKeyboardFocusVisible(true);
      }

      onKeyDown?.(event);
    };

    const handleMenuClose = () => {
      setIsKeyboardFocusVisible(false);
      onMenuClose?.();
    };

    const providedInput = providedComponents?.Input;
    const providedDummyInput = providedComponents?.DummyInput;

    const InputComponent = useMemo(() => withExposedComboboxValue(providedInput ?? components.Input), [providedInput]);
    const DummyInputComponent = useMemo(
      () => withExposedDummyComboboxValue(providedDummyInput ?? components.DummyInput),
      [providedDummyInput]
    );

    const selectComponents = useMemo(
      () => ({
        DropdownIndicator: DropdownIndicatorComponent,
        Option: OptionComponent,
        Placeholder: PlaceholderComponent,
        SingleValue: SingleValueComponent,
        ...providedComponents,
        Input: InputComponent,
        DummyInput: DummyInputComponent,
      }),
      [providedComponents, InputComponent, DummyInputComponent]
    );

    return (
      <InputWrapper
        htmlFor={resolvedInputId}
        label={label}
        errorText={errorText}
        helperText={helperText}
        isError={isError}
        isOptional={isOptional}
        dataTestId={`${dataTestId}-container`}
        containerClassName={classNames(containerClassName, styles.container)}
        wrapperClassName={classNames(wrapperClassName, styles.wrapper)}
        isHelperTextVisible={isHelperTextVisible}
        showRequiredAsterisk={showRequiredAsterisk}
      >
        <ReactSelect<SelectOptionItem, false, SelectGroup>
          ref={ref}
          inputId={resolvedInputId}
          data-testid={dataTestId}
          className={classNames(styles.inputContainer, {
            [styles.containerError]: isError,
            [styles.hideCheckIcon]: !hasLeadingIcon,
          })}
          isOptionDisabled={(option: SelectOptionItem) => option?.readonly ?? false}
          classNamePrefix="react-select"
          unstyled={true}
          components={selectComponents}
          classNames={{
            ...selectClassNames,
            option: (props) =>
              classNames(selectClassNames?.option?.(props), {
                [styles.keyboardFocusedOption]: isKeyboardFocusVisible && props.isFocused,
              }),
          }}
          noOptionsMessage={() => __("libs.ui.select.no_options")}
          placeholder={placeholder ?? __("libs.ui.select.placeholder")}
          aria-label={ariaLabel || label}
          onKeyDown={handleKeyDown}
          onMenuClose={handleMenuClose}
          {...rest}
        />
      </InputWrapper>
    );
  }
);
